<script>
import * as lodash from "lodash";
import { preComputeData } from "../../preComputeData";
import { computeStls } from "../../computeStls";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import FormField from "../components/FormField.vue";
import OpenFlexureHeader from "../components/OpenFlexureHeader.vue";
import "gb3d";

import { computeDisabledOptions } from "../../computeDisabledOptions";

const VUE_APP_IS_CLI = process.env.VUE_APP_IS_CLI === "true";

export default {
  name: "StlSelector",
  components: {
    FormField,
    OpenFlexureHeader,
  },
  data() {
    const { buildVersion } = this.$route.params;
    const fullUrl = window.location;
    return {
      loading: true,
      optionData: {},
      fullUrl,
      buildVersion,
      surprisingOptions: [],
      isUrlCopied: false,
      isGeneratingZip: false,
      preview3dAll: false,
    };
  },
  beforeRouteEnter(to, from, next) {
    const { buildVersion } = to.params;
    const url = VUE_APP_IS_CLI
      ? "http://localhost:8888/stls/stl_options.json"
      : `https://build.openflexure.org/openflexure-microscope/${buildVersion}/models/stl_options.json`;
    return fetch(url, { mode: "cors" })
      .then((r) => r.json())
      .then((stlOptions) => {
        next((vm) => vm.setPreComputedData(stlOptions));
      });
  },
  computed: {
    stls() {
      return computeStls(this.preComputedData.allStls, this.optionData).filter(
        (stl) => !this.preComputedData.neverChanging.includes(stl)
      );
    },
    buildUrl() {
      if (VUE_APP_IS_CLI) {
        return "http://localhost:8888/stls/";
      } else {
        return `https://build.openflexure.org/openflexure-microscope/${this.buildVersion}/models/`;
      }
    },
    presetSelected() {
      return (
        this.preComputedData.presets.find((p) => {
          for (const k in p.parameters) {
            if (this.optionData[k] !== p.parameters[k]) {
              return false;
            }
          }
          return true;
        }) || {}
      );
    },
    disabledAndNoEffectOptions() {
      return computeDisabledOptions(
        this.preComputedData.allStls,
        this.preComputedData.originalOptions,
        this.optionData,
        this.preComputedData.required
      );
    },
    disabledOptions() {
      return {
        ...this.preComputedData.emptyOptions,
        ...this.disabledAndNoEffectOptions?.disabled,
      };
    },
    noEffectOptions() {
      return {
        ...this.preComputedData.emptyOptions,
        ...this.disabledAndNoEffectOptions?.noEffect,
      };
    },
  },
  watch: {
    $route(to) {
      this.buildVersion = to.params.buildVersion;
      this.optionData = {
        ...this.optionData,
        ...parseQuery(to.query, this.preComputedData.originalOptions),
      };
    },
    optionData: {
      handler(to, from) {
        // vuejs 'deep: true' didn't work all the time so we check for equality
        // with lodash  to avoid redundant navigation warnings (and spread
        // i.e. copy the objects to remove vuejs magic)
        if (!lodash.isEqual({ ...to }, { ...from })) {
          this.$router.push({ query: to });
          this.fullUrl = window.location;
        }
      },
    },
  },
  methods: {
    setPreComputedData(stlOptions) {
      const preComputedData = preComputeData(stlOptions);
      const optionData = {
        ...preComputedData.optionData,
        ...parseQuery(this.$route.query, preComputedData.originalOptions),
      };
      // default all 3d previews to false, i.e. hidden by default
      const preview3d = preComputedData.allStls.reduce(
        (preview3d, x) => ({
          ...preview3d,
          [x.stl]: false,
        }),
        {}
      );
      preComputedData.emptyOptions = {};
      for (const key in preComputedData.originalOptions) {
        preComputedData.emptyOptions[key] = [];
      }
      this.preComputedData = preComputedData;
      this.preview3d = preview3d;
      this.optionData = optionData;
      this.loading = false;
    },

    setPreset(key) {
      const preset = this.preComputedData.presets.find((p) => p.key === key);
      const optionData = { ...this.optionData, ...preset.parameters };
      this.optionData = optionData;
    },

    generateZip() {
      this.isGeneratingZip = true;
      const stls = this.stls.concat(this.preComputedData.neverChanging);
      return Promise.all(
        stls.map((stl) =>
          fetch(this.buildUrl + stl, { mode: "cors" })
            .then((r) => r.blob())
            .then((blob) => [stl, blob])
        )
      ).then((results) => {
        const zip = new JSZip();
        for (const [stl, blob] of results) {
          zip
            .folder("openflexure-microscope-stls-" + this.buildVersion)
            .file(stl, blob);
        }
        return zip.generateAsync({ type: "blob" }).then((blob) => {
          this.isGeneratingZip = false;
          saveAs(blob, `openflexure-microscope-stls-${this.buildVersion}.zip`);
        });
      });
    },

    setOption(key, value, isInvalid) {
      // set the option if it results in a valid configuration
      if (!isInvalid) {
        // we assign a copy using spreads instead of using $set because $set
        // doesn't seem to trigger watch
        this.optionData = { ...this.optionData, [key]: value };
      } else {
        // compute the least invasive change to make in the options to allow
        // setting this option to the desired value

        let valid = [];
        for (const [group, validGroupOptions] of this.preComputedData.valid) {
          if (group.has(key)) {
            valid = validGroupOptions
              .filter((v) => v[key] === value)
              .map((v) => ({
                ...this.optionData,
                ...v,
              }));
            break;
          }
        }

        valid.sort((a, b) => {
          const countA = countEqual(a, this.optionData);
          const countB = countEqual(b, this.optionData);
          if (countA < countB) {
            return 1;
          }
          if (countA > countB) {
            return -1;
          }
          return 0;
        });
        const leastChanged = valid[0];

        this.surprisingOptions = [];
        for (const k in leastChanged) {
          if (leastChanged[k] !== this.optionData[k]) {
            const value = leastChanged[k];
            const valueName =
              this.preComputedData.options[k] === "bool"
                ? String(value)
                : this.preComputedData.options[k].find((o) => o.value === value)
                    .text;
            this.surprisingOptions.push([k, value, valueName]);
          }
        }
      }
    },

    togglePreview3d(name) {
      this.preview3d[name] = !this.preview3d[name];
    },

    togglePreview3dAll() {
      this.preview3dAll = !this.preview3dAll;
    },

    clearSurprisingOptions() {
      this.surprisingOptions = [];
    },

    confirmSurprisingOptions() {
      // we assign copies using spread instead of using $set because $set
      // doesn't seem to trigger watch
      const optionData = { ...this.optionData };
      for (const [k, v] of this.surprisingOptions) {
        optionData[k] = v;
      }
      // asssigning a new object like this does trigger watch as we expect
      this.optionData = optionData;
      this.surprisingOptions = [];
    },

    copyLink() {
      this.$copyText(this.fullUrl).then(() => {
        this.isUrlCopied = true;
        setTimeout(() => {
          this.isUrlCopied = false;
        }, 2000);
      });
    },
  },
};

function parseQuery(query, options) {
  const parsed = {};
  for (const key in options) {
    let v = query[key];
    if (v != null && v !== "") {
      if (options[key] === "bool") {
        if (v !== true && v !== false) {
          if (v === "false" || parseInt(v, 10) === 0) {
            v = false;
          } else {
            v = true;
          }
        }
        parsed[key] = v;
      } else if (options[key].includes(v)) {
        parsed[key] = v;
      }
    }
  }
  return parsed;
}

function countEqual(x, y) {
  let count = 0;
  for (const key in y) {
    if (x[key] === y[key]) {
      count += 1;
    }
  }
  return count;
}
</script>

<template>
  <div>
    <OpenFlexureHeader />
    <div class="main-content-container">
      <sui-segment style="width: 100%; height: 100vh;" v-if="loading">
        <sui-loader active size="massive" />
      </sui-segment>
      <div v-else class="main-content">
        <sui-modal
          :open="surprisingOptions.length > 0"
          @clickAwayModal="clearSurprisingOptions"
        >
          <sui-modal-header>Change additional options?</sui-modal-header>
          <sui-modal-content>
            Your desired change can only be made if we make additional changes.
            We will set:
            <ul>
              <li
                v-for="[key, value, valueName] in surprisingOptions"
                :key="key"
              >
                {{ preComputedData.optionNames[key] }} to "{{ valueName }}"
              </li>
            </ul>
          </sui-modal-content>
          <sui-modal-actions>
            <sui-button negative @click.native="clearSurprisingOptions">
              Cancel
            </sui-button>
            <sui-button positive @click.native="confirmSurprisingOptions">
              OK
            </sui-button>
          </sui-modal-actions>
        </sui-modal>

        <div style="margin: 20px; max-width: 350px; min-width: 350px;">
          <h1>OpenFlexure Microscope STLs</h1>
          <p>
            This is a configurator for the
            <a href="https://openflexure.org/projects/microscope/"
              >OpenFlexure Microscope</a
            >
            a low-cost 3D-printed microscope with a high quality mechanical
            stage.
          </p>
          <p>
            Select the microscope configuration you would like below to download
            the right STL files to 3D-print.
          </p>
          <sui-divider />
          <sui-divider />
          <h3>Presets</h3>
          <p>
            Select a common configuration of the microscope that will set
            multiple options at once.
          </p>
          <sui-form>
            <sui-form-fields grouped>
              <sui-form-field
                v-for="preset in preComputedData.presets"
                :key="preset.key"
              >
                <sui-checkbox
                  radio
                  :name="preset.key"
                  :input-value="presetSelected.key === preset.key"
                  @change="setPreset(preset.key)"
                >
                  <div style="font-weight: bold;">
                    {{ preset.title }}
                  </div>
                  {{ preset.description }}
                </sui-checkbox>
              </sui-form-field>
            </sui-form-fields>
          </sui-form>
          <sui-divider />
          <sui-divider />
          <sui-form>
            <h3>Options</h3>
            <FormField
              v-for="key in preComputedData.optionOrder"
              :key="key"
              :options="preComputedData.options[key]"
              :option-name="preComputedData.optionNames[key]"
              :disabled="disabledOptions[key]"
              :no-effect="noEffectOptions[key]"
              :value="optionData[key]"
              :description="preComputedData.descriptions[key]"
              @change="(v, disabled) => setOption(key, v, disabled)"
            />
            <template v-if="preComputedData.advancedOptionOrder.length > 0">
              <sui-divider />
              <details>
                <summary style="cursor: pointer; font-weight: bold;"
                  >Advanced</summary
                >
                <sui-divider />
                <FormField
                  v-for="key in preComputedData.advancedOptionOrder"
                  :key="key"
                  :options="preComputedData.options[key]"
                  :option-name="preComputedData.optionNames[key]"
                  :disabled="disabledOptions[key]"
                  :no-effect="noEffectOptions[key]"
                  :value="optionData[key]"
                  :description="preComputedData.descriptions[key]"
                  @change="(v, disabled) => setOption(key, v, disabled)"
                />
              </details>
              <sui-divider />
              <sui-divider />
            </template>
          </sui-form>
          <sui-button size="tiny" basic icon="copy" @click="copyLink"
            ><sui-icon name="chain" />copy config as shareable link</sui-button
          >
          <sui-label v-if="isUrlCopied">copied!</sui-label>
        </div>
        <div>
          <div
            style="
              width: 100%;
              display: flex;
              justify-content: flex-end;
              padding: 20px;
            "
          >
            <a
              :href="
                'https://build.openflexure.org/openflexure-microscope/' +
                buildVersion +
                /docs/
              "
            >
              <sui-button basic>
                Assembly Instructions <sui-icon name="right arrow" />
              </sui-button>
            </a>
          </div>

          <sui-segment class="stl-panel">
            <div
              style="
                width: 100%;
                display: flex;
                justify-content: center;
                margin-bottom: 20px;
                margin-top: 20px;
              "
            >
              <sui-button
                size="large"
                :icon="preview3dAll ? 'check square outline' : 'square outline'"
                basic
                @click="togglePreview3dAll"
              >
                Preview All
              </sui-button>
              <sui-button
                size="huge"
                basic
                primary
                icon="download"
                :loading="isGeneratingZip"
                @click="generateZip"
                >Download</sui-button
              >
            </div>
            <div>
              <h5 style="margin-top: 5px;">Customisable:</h5>
              <sui-card-group :items-per-row="4" class="doubling">
                <!-- I tried to put this into a component but it broke gb3d-viewer -->
                <sui-card v-for="name in stls" :key="name" class="stl-card">
                  <sui-card-content>
                    <div style="display: flex; flex-direction: column;">
                      <div
                        style="
                          width: 100%;
                          height: 200px;
                          display: flex;
                          justify-content: center;
                          align-items: center;
                          border: 1px solid #eee;
                        "
                      >
                        <template v-if="preview3dAll || preview3d[name]">
                          <gb3d-viewer
                            show-filelink="false"
                            style="width: 100%; height: 200px;"
                            :src="buildUrl + name"
                          />
                        </template>
                        <template v-else>
                          <div>
                            <sui-button
                              basic
                              @click.native="togglePreview3d(name)"
                              >Preview</sui-button
                            >
                          </div>
                        </template>
                      </div>
                    </div>
                    <div style="overflow: hidden; text-overflow: ellipsis;">
                      <a
                        style="font-size: 15px; text-overflow: ellipsis;"
                        :href="buildUrl + name"
                        >{{ name }}</a
                      >
                    </div>
                  </sui-card-content>
                </sui-card>
              </sui-card-group>
            </div>
            <div>
              <h5 style="margin-top: 40px;">Always Included:</h5>
              <sui-card-group :items-per-row="4" class="doubling">
                <!-- I tried to put this into a component but it broke gb3d-viewer -->
                <sui-card
                  v-for="name in preComputedData.neverChanging"
                  :key="name"
                  class="stl-card"
                >
                  <sui-card-content>
                    <div style="display: flex; flex-direction: column;">
                      <div
                        style="
                          width: 100%;
                          height: 200px;
                          display: flex;
                          justify-content: center;
                          align-items: center;
                          border: 1px solid #eee;
                        "
                      >
                        <template v-if="preview3dAll || preview3d[name]">
                          <gb3d-viewer
                            show-filelink="false"
                            style="width: 100%; height: 200px;"
                            :src="buildUrl + name"
                          />
                        </template>
                        <template v-else>
                          <div>
                            <sui-button
                              basic
                              @click.native="togglePreview3d(name)"
                              >Preview</sui-button
                            >
                          </div>
                        </template>
                      </div>
                    </div>
                    <div style="overflow: hidden; text-overflow: ellipsis;">
                      <a
                        style="font-size: 15px; text-overflow: ellipsis;"
                        :href="buildUrl + name"
                        >{{ name }}</a
                      >
                    </div>
                  </sui-card-content>
                </sui-card>
              </sui-card-group>
            </div>
            <sui-divider />
            <div
              style="
                display: flex;
                justify-content: flex-end;
                margin-bottom: 10px;
              "
            >
              <a :href="buildUrl">browse all</a>
            </div>
          </sui-segment>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped src="normalize.css/normalize.css"></style>

<style src="semantic-ui-css/semantic.css"></style>

<style scoped>
.main-content-container {
  display: flex;
  justify-content: center;
}

.main-content {
  display: flex;
  margin: 20px;
  justify-content: center;
  flex-grow: 1;
}

.stl-panel {
  margin: 20px;
  margin-bottom: 0px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  max-width: 1318px;
}

.stl-card {
  border: none !important;
  box-shadow: none !important;
}
</style>
